import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import Renderable from '../../../Component/Renderable';
import Scissor from '../../../Prototype/Level/Card/Scissors';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import ScissorCard from '../../../Prototype/Level/Card/ScissorsCard';
import Sprite from '../../../Prototype/Sprite';
import Point from '../../../Utility/Point';
import SpriteComponent from '../../../Component/Render/Sprite';
import { AwaitIterators, StartAnimationAsync } from '../AnimationHelper';
import Scene from '../../../Scene/Scene';
import { delay } from '../../../Utility/Task/TaskQueue';
import AnimationComponent from '../../../Component/Animation';
import CardSystem from '../../Card/CardSystem';

interface ICardScissorsMeta {
    scene: Scene;
    sourceCard: ScissorCard;
    targetCards: BaseCard[];
    cardScore: number;
    system: CardSystem;
}

export class CardScissors extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardScissorsMeta
    ): IterableIterator<number> {
        animator.addChild(meta.sourceCard);

        // instead of destroy, change sprite
        // meta.sourceCard.find('cardFrontSprite/ropeContainer').destroy();

        //const overlay = animator.getChildByName('overlay');
        //StartAnimation(overlay, 'OverlayFadeIn');

        const transform = meta.sourceCard.getComponent(TransformComponent);

        meta.scene.emit('UpdateScoreProgress');

        const crabJumps = [];
        const interval: number = 100;
        let i: number = 0;
        for (let card of meta.targetCards) {
            animator.addChild(card);

            // Add as child to animator to bring in front of overlay.
            crabJumps.push(
                this.crabJump(
                    animator,
                    card,
                    meta.sourceCard,
                    meta.cardScore,
                    i * interval
                )
            );
            i++;
        }

        yield* AwaitIterators(crabJumps);

        meta.system.sortCardsInPlay(
            meta.system.store.getState().levelState.cardsState
        );
        //yield* PlayAnimation(overlay, 'OverlayFadeOut');
    }

    private *crabJump(
        animator: GameObject,
        targetCard: BaseCard,
        sourceCard: ScissorCard,
        cardScore: number,
        initialDelay: number
    ): IterableIterator<number> {
        try {
            yield* delay(initialDelay);
            const targetCardTransform = targetCard.getComponent(
                TransformComponent
            );

            //targetCardComponent = targetCard.getComponent(CardComponent);
            const ropeContainer = targetCard.find('modifierContainer/rope');
            const ropeSprite = ropeContainer.find('ropeSprite');
            const ropeSpriteComponent = ropeSprite.getComponent(
                SpriteComponent
            );

            // Create another rope sprite for texture cross-fade.
            const prevRopeSprite = GameObject.Instantiate(Sprite, {
                name: 'prevRopeSprite',
                texture: ropeSpriteComponent.texture, // Set to current rope texture.
                anchor: new Point(0.5, 0.5),
                scale: new Point(1, 1),
            });

            const prevRopeSpriteComponent = prevRopeSprite.getComponent(
                SpriteComponent
            );

            // Hide current rope sprite and add the *new* previous sprite
            // ropeSpriteComponent.alpha = 0;
            // ropeContainer.addChild(prevRopeSprite);
            const ropeTextures = [
                'ropes_blocked_1',
                'ropes_blocked_2',
                'ropes_blocked_3',
                'blank',
            ];
            const breakingPoints: number[] = [-103, -140, -172];

            const nextTextureIndex =
                ropeTextures.indexOf(ropeSpriteComponent.texture) + 1;

            const breakingRope: GameObject = GameObject.Instantiate(Sprite, {
                name: 'breaking_rope',
                texture: 'rope_animation0',
                x: -109,
                y: breakingPoints[nextTextureIndex - 1],
                piviot: Point.Center,
            });

            const prevRopeTweenAlpha = this.getTween(prevRopeSpriteComponent);
            const currentRopeTweenAlpha = this.getTween(ropeSpriteComponent);
            const sourceCardTransform = sourceCard.getComponent(
                TransformComponent
            );

            const sourceCardRenderable = sourceCard.getComponent(Renderable);
            const sourceCardAlphaTween = this.getTween(sourceCardRenderable);

            const scissors = GameObject.Instantiate(Scissor, {
                clipName: 'ScissorsWiggle',
            });
            const scissorsTransform = scissors.getComponent(TransformComponent);
            const scissorsRenderable = scissors.getComponent(Renderable);
            const scissorsTweenAlpha = this.getTween(scissorsRenderable);
            const scissorsTweenX = this.getTween(scissorsTransform);
            const scissorsTweenY = this.getTween(scissorsTransform);

            animator.addChild(scissors);

            // Position scissors on the source card
            scissorsTransform.position.copy(
                sourceCardTransform.position as Point
            );

            const jumpDuration = 750;
            const snipDuration = 500;
            const fadeDuration = 150;

            const tweenHeight = Math.min(
                targetCardTransform.y - 50,
                sourceCardTransform.y - 200
            );

            sourceCardAlphaTween
                .to({ alpha: 0 }, fadeDuration)
                .start(this.elapsedTime);

            // Transition to the card
            scissorsTweenX
                .to({ x: targetCardTransform.x }, jumpDuration)
                .easing(Easing.Quadratic.Out)
                .start(this.elapsedTime);

            scissorsTweenY
                .to({ y: tweenHeight }, jumpDuration * 0.5)
                .easing(Easing.Quadratic.Out);

            yield* this.startTween(scissorsTweenY);

            // Fall downwards
            scissorsTweenY
                .to({ y: targetCardTransform.y + 100 }, jumpDuration * 0.5)
                .easing(Easing.Quadratic.In);

            yield* this.startTween(scissorsTweenY);

            breakingRope.addComponent(AnimationComponent);
            ropeSpriteComponent.gameObject.addChild(breakingRope);

            if (ropeTextures[nextTextureIndex]) {
                ropeSpriteComponent.texture = ropeTextures[nextTextureIndex];
            } else {
                ropeSpriteComponent.visible = false;
            }
            StartAnimationAsync(breakingRope, 'RopeBreak');
            // Cross-fade rope textures.
            prevRopeSpriteComponent.alpha = 1;

            prevRopeTweenAlpha
                .to({ alpha: 0 }, fadeDuration)
                .start(this.elapsedTime);
            currentRopeTweenAlpha
                .to({ alpha: 1 }, fadeDuration)
                .start(this.elapsedTime);

            // Cut upwards
            scissorsTweenY
                .to({ y: targetCardTransform.y - 125 }, snipDuration)
                .easing(Easing.Quadratic.InOut);

            yield* this.startTween(scissorsTweenY);

            // Fade scissors
            scissorsTweenAlpha.to({ alpha: 0 }, 100);
            yield* this.startTween(scissorsTweenAlpha);

            scissors.destroy();
            breakingRope.destroy();
            prevRopeSprite.destroy();
        } catch (e) {
            console.error(e);
        }
    }
}
